// src/pages/broadcast.lib.ts —— Bot 广告推送（06 C-33）纯函数：文案白名单解析（预览分段 / 字数计数）、按钮 URL 本地预检、配置时区与 UTC 互转。
// 安全：预览只产出「文本 + 样式」分段，由模板按文本节点渲染（不拼 HTML、不经 innerHTML）；白名单外的标签原样作为文本显示（与后端 html.EscapeString 口径一致）。
// 本地校验只是提示，服务端（render.go 白名单重建 / 按钮校验 / 1024·4096 超长 4220 text_too_long）为准。

// ---- 文案白名单 <b><i><u><s><code><a href="https://…"> ----

export interface RichSeg {
  text: string;
  b: boolean;
  i: boolean;
  u: boolean;
  s: boolean;
  code: boolean;
  href: string | null; // 仅 https 链接；其余 href 不作链接（后端丢弃）
}

type Flag = 'b' | 'i' | 'u' | 's' | 'code';

const SIMPLE_RE = /^<(\/?)(b|i|u|s|code)>/i;
const A_OPEN_RE = /^<a\s+href\s*=\s*"([^"]*)"\s*>/i;
const A_CLOSE_RE = /^<\/a>/i;
const ENTITY_RE = /&(lt|gt|amp|quot|#39|#\d{1,7}|#x[0-9a-f]{1,6});/gi;

const NAMED: Readonly<Record<string, string>> = { lt: '<', gt: '>', amp: '&', quot: '"', '#39': "'" };

export function decodeEntities(s: string): string {
  return s.replace(ENTITY_RE, (m, name: string) => {
    const k = name.toLowerCase();
    const hit = NAMED[k];
    if (hit !== undefined) return hit;
    const code = k.startsWith('#x') ? Number.parseInt(k.slice(2), 16) : Number.parseInt(k.slice(1), 10);
    return Number.isInteger(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : m;
  });
}

export function isHttps(raw: string): boolean {
  try {
    const u = new URL(raw);
    return u.protocol === 'https:' && !u.username && !u.password;
  } catch {
    return false;
  }
}

export function richSegments(src: string): RichSeg[] {
  const depth: Record<Flag, number> = { b: 0, i: 0, u: 0, s: 0, code: 0 };
  const hrefs: Array<string | null> = [];
  const out: RichSeg[] = [];
  let buf = '';
  const flush = (): void => {
    if (!buf) return;
    const href = hrefs.length > 0 ? hrefs[hrefs.length - 1] ?? null : null;
    out.push({ text: decodeEntities(buf), b: depth.b > 0, i: depth.i > 0, u: depth.u > 0, s: depth.s > 0, code: depth.code > 0, href });
    buf = '';
  };
  let k = 0;
  while (k < src.length) {
    const lt = src.indexOf('<', k);
    if (lt < 0) {
      buf += src.slice(k);
      break;
    }
    buf += src.slice(k, lt);
    const rest = src.slice(lt);
    const simple = SIMPLE_RE.exec(rest);
    const aOpen = simple ? null : A_OPEN_RE.exec(rest);
    const aClose = simple || aOpen ? null : A_CLOSE_RE.exec(rest);
    const tag = simple ?? aOpen ?? aClose;
    if (!tag) {
      buf += '<'; // 白名单外：按字面文本
      k = lt + 1;
      continue;
    }
    flush();
    if (simple) {
      const f = (simple[2] ?? '').toLowerCase();
      if (f === 'b' || f === 'i' || f === 'u' || f === 's' || f === 'code') depth[f] = Math.max(0, depth[f] + (simple[1] ? -1 : 1));
    } else if (aOpen) {
      const href = decodeEntities(aOpen[1] ?? '');
      hrefs.push(isHttps(href) ? href : null);
    } else hrefs.pop();
    k = lt + tag[0].length;
  }
  flush();
  return out;
}

// Telegram 计数口径：去白名单标签并实体解码后的可见文本 UTF-16 长度
export function visibleLength(src: string): number {
  return richSegments(src).reduce((n, s) => n + s.text.length, 0);
}

// 强制退订尾注（规格决定 5）：正文后空一行追加
export const FOOTER_ZH = '回复 /stop_ads 退订推广消息';
export const FOOTER_EN = 'Reply /stop_ads to unsubscribe';
const FOOTER_SEP = 2; // "\n\n"

export const CAPTION_MAX = 1024; // 带图（caption）
export const TEXT_MAX = 4096; // 无图

export interface TextCount {
  n: number; // 正文 + 尾注
  max: number;
  over: boolean;
}

// footer：zh / en = 该语言尾注；max = 英文文案为空时中文正文要同时下发给中英用户，按两种尾注较长者计（与后端口径一致）
export type FooterMode = 'zh' | 'en' | 'max';

export function footerLength(mode: FooterMode): number {
  return mode === 'zh' ? FOOTER_ZH.length : mode === 'en' ? FOOTER_EN.length : Math.max(FOOTER_ZH.length, FOOTER_EN.length);
}

export function countText(src: string, footer: FooterMode, hasImage: boolean): TextCount {
  const n = visibleLength(src) + FOOTER_SEP + footerLength(footer);
  const max = hasImage ? CAPTION_MAX : TEXT_MAX;
  return { n, max, over: n > max };
}

export function segStyle(s: RichSeg): string {
  const deco = [s.u || s.href ? 'underline' : '', s.s ? 'line-through' : ''].filter(Boolean).join(' ');
  return [
    s.b ? 'font-weight:800' : '',
    s.i ? 'font-style:italic' : '',
    deco ? `text-decoration:${deco}` : '',
    s.code ? "font-family:'JetBrains Mono',monospace;background:#eef3f0;border-radius:4px;padding:0 3px" : '',
    s.href ? 'color:#1f6fa8' : '',
  ]
    .filter(Boolean)
    .join(';');
}

// ---- 按钮 URL 本地预检（规格决定 6） ----

export type UrlProblem = '' | 'invalid' | 'https' | 'userinfo' | 'tme';

const TME_HOSTS: ReadonlySet<string> = new Set(['t.me', 'telegram.me', 'www.t.me', 'www.telegram.me']);
const TME_PATH_RE = /^\/[A-Za-z][A-Za-z0-9_]{3,31}\/?$/;

export function buttonUrlProblem(raw: string): UrlProblem {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return 'invalid';
  }
  if (u.protocol !== 'https:') return 'https';
  if (u.username || u.password) return 'userinfo';
  if (TME_HOSTS.has(u.hostname.toLowerCase())) {
    if (!TME_PATH_RE.test(u.pathname) || u.pathname.toLowerCase().startsWith('/joinchat')) return 'tme';
    for (const k of u.searchParams.keys()) if (k !== 'start') return 'tme';
  }
  return '';
}

// ---- 配置时区（system_configs.timezone）与 UTC 互转 ----

const fmtCache = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat | null {
  const hit = fmtCache.get(tz);
  if (hit) return hit;
  try {
    const f = new Intl.DateTimeFormat('en-CA', { timeZone: tz || undefined, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
    fmtCache.set(tz, f);
    return f;
  } catch (e) {
    console.error('[gp:bc] 非法时区，按设备本地时区换算', tz, e);
    return null;
  }
}

// 该时刻在 tz 的墙钟（按 UTC 解释的毫秒）减去真实毫秒 = 偏移
function offsetMs(ms: number, tz: string): number {
  const f = formatter(tz);
  if (!f) return -new Date(ms).getTimezoneOffset() * 60000;
  const p: Record<string, number> = {};
  for (const x of f.formatToParts(new Date(ms))) if (x.type !== 'literal') p[x.type] = Number(x.value);
  const wall = Date.UTC(p['year'] ?? 1970, (p['month'] ?? 1) - 1, p['day'] ?? 1, p['hour'] ?? 0, p['minute'] ?? 0, p['second'] ?? 0);
  return wall - Math.floor(ms / 1000) * 1000;
}

const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

// datetime-local 值（配置时区墙钟）→ RFC3339 UTC；两次求偏移以覆盖 DST 边界
export function localToUtc(local: string, tz: string): string | null {
  const m = LOCAL_RE.exec(local.trim());
  if (!m) return null;
  const guess = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  if (!Number.isFinite(guess)) return null;
  let t = guess - offsetMs(guess, tz);
  t = guess - offsetMs(t, tz);
  return new Date(t).toISOString();
}

// RFC3339 → 配置时区 datetime-local 值；非法返回 ''
export function utcToLocal(iso: string | null | undefined, tz: string): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms + offsetMs(ms, tz));
  return d.toISOString().slice(0, 16);
}

export const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
